import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "horizontal" | "compact" | "overlay";
}

const categoryStyle: Record<string, string> = {
  star: "bg-accent-100 text-accent-800",
  film: "bg-primary-100 text-primary-800",
  music: "bg-secondary-100 text-secondary-800",
  creator: "bg-primary-100 text-primary-800",
  fashion: "bg-accent-100 text-accent-800",
  gossip: "bg-secondary-100 text-secondary-800",
};

function CategoryBadge({ article }: { article: Article }) {
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${
        categoryStyle[article.category] ?? "bg-background-200 text-foreground-800"
      }`}
    >
      {article.categoryName}
    </span>
  );
}

function Meta({ article, light = false }: { article: Article; light?: boolean }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${
        light ? "text-background-200" : "text-foreground-500"
      }`}
    >
      <span className="flex items-center gap-1">
        <i className="ri-user-3-line" aria-hidden="true"></i>
        {article.author}
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-calendar-line" aria-hidden="true"></i>
        {article.date}
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-time-line" aria-hidden="true"></i>
        {article.readTime}
      </span>
    </div>
  );
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "overlay") {
    return (
      <article className="group relative overflow-hidden rounded-lg">
        <div className="h-72 w-full overflow-hidden md:h-80">
          <img
            src={article.cover}
            alt={`${article.title} · 瓜网${article.categoryName}`}
            title={`${article.title} · 瓜网${article.categoryName}`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/35 to-transparent"></div>
        <div className="absolute inset-x-0 bottom-0 p-6">
          <CategoryBadge article={article} />
          <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-background-50 md:text-2xl">
            <Link to={`/article/${article.slug}`} className="hover:text-primary-200">
              {article.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm text-background-200">{article.excerpt}</p>
          <div className="mt-3">
            <Meta article={article} light />
          </div>
        </div>
      </article>
    );
  }

  if (variant === "horizontal") {
    return (
      <article className="group flex gap-4">
        <Link
          to={`/article/${article.slug}`}
          className="h-24 w-32 flex-shrink-0 overflow-hidden rounded-lg md:h-28 md:w-40"
        >
          <img
            src={article.cover}
            alt={`${article.title} · 瓜网${article.categoryName}`}
            title={`${article.title} · 瓜网${article.categoryName}`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="min-w-0 flex-1">
          <CategoryBadge article={article} />
          <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950">
            <Link to={`/article/${article.slug}`} className="line-clamp-2 hover:text-primary-700">
              {article.title}
            </Link>
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm text-foreground-600">{article.excerpt}</p>
        </div>
      </article>
    );
  }

  if (variant === "compact") {
    return (
      <article className="group border-b border-background-200 pb-5 last:border-none last:pb-0">
        <CategoryBadge article={article} />
        <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950">
          <Link to={`/article/${article.slug}`} className="hover:text-primary-700">
            {article.title}
          </Link>
        </h3>
        <div className="mt-2">
          <Meta article={article} />
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/article/${article.slug}`} className="block h-48 w-full overflow-hidden sm:h-52">
        <img
          src={article.cover}
          alt={`${article.title} · 瓜网${article.categoryName}`}
          title={`${article.title} · 瓜网${article.categoryName}`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <CategoryBadge article={article} />
        <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
          <Link to={`/article/${article.slug}`} className="line-clamp-2 hover:text-primary-700">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.excerpt}
        </p>
        <div className="mt-4 border-t border-background-200 pt-3">
          <Meta article={article} />
        </div>
      </div>
    </article>
  );
}